import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DOMAIN_LABELS, DOMAIN_ACTIONS } from '@/lib/permissions';
import { useTranslation } from 'react-i18next';

interface CreateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cloneFrom?: {
    name: string;
    description: string | null;
    level: number;
    permissions: { domain: string; action: string; scope: string }[];
  } | null;
}

export function CreateRoleDialog({ open, onOpenChange, cloneFrom }: CreateRoleDialogProps) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState(cloneFrom ? t('hire.roles.copyName', { name: cloneFrom.name }) : '');
  const [description, setDescription] = useState(cloneFrom?.description || '');
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(
    new Set(cloneFrom?.permissions.map(p => `${p.domain}:${p.action}`) || [])
  );

  // Auto-calculate level based on permissions selected
  const computeLevel = (perms: Set<string>): number => {
    const totalPossible = Object.entries(DOMAIN_ACTIONS).reduce((sum, [, actions]) => sum + actions.length, 0);
    const ratio = perms.size / totalPossible;
    // Scale from 10 (minimal perms) to 80 (near-full perms), minimum 10 if any perms selected
    if (perms.size === 0) return 10;
    return Math.min(80, Math.max(10, Math.round(ratio * 80)));
  };
  const level = computeLevel(selectedPerms);

  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const togglePerm = (key: string) => {
    setSelectedPerms(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleDomain = (domain: string) => {
    const actions = DOMAIN_ACTIONS[domain] || [];
    const allKeys = actions.map(a => `${domain}:${a}`);
    const allSelected = allKeys.every(k => selectedPerms.has(k));
    setSelectedPerms(prev => {
      const next = new Set(prev);
      allKeys.forEach(k => allSelected ? next.delete(k) : next.add(k));
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim() || !profile?.org_id) return;
    setSaving(true);
    try {
      // Create role definition
      const { data: role, error: roleErr } = await supabase
        .from('role_definitions')
        .insert({
          slug,
          name: name.trim(),
          description: description.trim() || null,
          base_role: 'viewer',
          level,
          is_system: false,
          org_id: profile.org_id,
        })
        .select('id')
        .single();

      if (roleErr) throw roleErr;

      // Insert permissions
      const permRows = Array.from(selectedPerms).map(key => {
        const [domain, action] = key.split(':');
        return { role_definition_id: role.id, domain, action, scope: 'all' };
      });

      if (permRows.length > 0) {
        const { error: permErr } = await supabase
          .from('role_permissions')
          .insert(permRows);
        if (permErr) throw permErr;
      }

      toast.success(t('hire.roles.createdToast'));
      queryClient.invalidateQueries({ queryKey: ['role-definitions'] });
      onOpenChange(false);
      resetForm();
    } catch (err: unknown) {
      toast.error(err instanceof Error && err.message ? err.message : t('hire.roles.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setSelectedPerms(new Set());
  };

  const domainLabel = (domain: string) => t(`hire.permissions.domains.${domain}`);
  const actionLabel = (action: string) => t(`hire.permissions.actions.${action}`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[90vh] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{cloneFrom ? t('hire.roles.cloneRole') : t('hire.roles.createCustomRole')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 min-h-0 overflow-hidden flex flex-col">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('hire.roles.roleName')}</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder={t('hire.roles.roleNamePlaceholder')} />
              {slug && <p className="text-xs text-muted-foreground">{t('hire.roles.slugLabel', { slug })}</p>}
            </div>
            <div className="space-y-2">
              <Label>{t('hire.roles.description')}</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t('hire.roles.descriptionPlaceholder')} rows={2} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label>{t('hire.permissions.title')}</Label>
            <Badge variant="outline" className="text-xs">{t('hire.roles.authorityLevel', { level })}</Badge>
          </div>
          <ScrollArea type="always" className="flex-1 min-h-0 border rounded-xl p-3 pe-4">
            <div className="space-y-4">
              {Object.keys(DOMAIN_LABELS).map((domain) => {
                const actions = DOMAIN_ACTIONS[domain] || [];
                const grantedCount = actions.filter(a => selectedPerms.has(`${domain}:${a}`)).length;
                const allSelected = grantedCount === actions.length;

                return (
                  <div key={domain} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={() => toggleDomain(domain)}
                      />
                      <span className="text-sm font-medium text-foreground">{domainLabel(domain)}</span>
                      <span className="text-xs text-muted-foreground">({grantedCount}/{actions.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 ms-6">
                      {actions.map(action => {
                        const key = `${domain}:${action}`;
                        const checked = selectedPerms.has(key);
                        return (
                          <Badge
                            key={action}
                            variant="outline"
                            className={`text-xs cursor-pointer select-none transition-colors ${
                              checked
                                ? 'bg-accent/10 text-accent border-accent/20'
                                : 'bg-muted/30 text-muted-foreground border-border opacity-60 hover:opacity-100'
                            }`}
                            onClick={() => togglePerm(key)}
                          >
                            {actionLabel(action)}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? t('hire.account.saving') : cloneFrom ? t('hire.roles.cloneRole') : t('hire.roles.createRole')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
